import { EnforcementStats } from 'types/CarTracking';

type DailyScansSeries = Array<{
    date: string;
    rentals: number;
    enforcementAutostarts: number;
}>;
type MockScansLookbackPeriod = '4w' | '3m' | '6m' | '12m';

function buildDailyScans(series: DailyScansSeries): EnforcementStats['daily_scans'] {
    return series.reduce<EnforcementStats['daily_scans']>((result, item) => {
        result[item.date] = {
            rentals: item.rentals,
            enforcement_autostarts: item.enforcementAutostarts,
            unique_scanned_plates: item.enforcementAutostarts,
        };
        return result;
    }, {});
}

function getWeekStartDateKey(dateKey: string) {
    const date = new Date(`${dateKey}T12:00:00Z`);
    date.setUTCHours(0, 0, 0, 0);

    const dayOfWeek = date.getUTCDay();
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    date.setUTCDate(date.getUTCDate() - daysSinceMonday);

    return date.toISOString().split('T')[0];
}

function buildWeeklyScans(dailyScans: EnforcementStats['daily_scans']): EnforcementStats['daily_scans'] {
    return Object.entries(dailyScans).reduce<EnforcementStats['daily_scans']>((result, [dateKey, scan]) => {
        const weekStartDateKey = getWeekStartDateKey(dateKey);
        const existingWeekScan = result[weekStartDateKey] ?? {
            rentals: 0,
            enforcement_autostarts: 0,
            unique_scanned_plates: 0,
        };

        result[weekStartDateKey] = {
            rentals: existingWeekScan.rentals + scan.rentals,
            enforcement_autostarts: existingWeekScan.enforcement_autostarts + scan.enforcement_autostarts,
            unique_scanned_plates: existingWeekScan.unique_scanned_plates + scan.unique_scanned_plates,
        };

        return result;
    }, {});
}

function buildWeeklyStats(stats: EnforcementStats): EnforcementStats {
    return {
        ...stats,
        series_granularity: 'week',
        daily_scans: buildWeeklyScans(stats.daily_scans),
    };
}

function buildRelativeSeries(
    values: Array<{ rentals: number; enforcementAutostarts: number }>,
    currentDate = new Date()
): DailyScansSeries {
    const endDate = new Date(currentDate);
    endDate.setUTCHours(0, 0, 0, 0);
    return values.map((v, index) => {
        const date = new Date(endDate);
        date.setUTCDate(endDate.getUTCDate() - (values.length - 1 - index));
        return { date: date.toISOString().split('T')[0], ...v };
    });
}

const fullSeriesValues = [
    { rentals: 37, enforcementAutostarts: 31 },
    { rentals: 38, enforcementAutostarts: 32 },
    { rentals: 39, enforcementAutostarts: 33 },
    { rentals: 39, enforcementAutostarts: 33 },
    { rentals: 40, enforcementAutostarts: 34 },
    { rentals: 41, enforcementAutostarts: 35 },
    { rentals: 42, enforcementAutostarts: 36 },
    { rentals: 44, enforcementAutostarts: 37 },
    { rentals: 45, enforcementAutostarts: 38 },
    { rentals: 46, enforcementAutostarts: 39 },
    { rentals: 47, enforcementAutostarts: 40 },
    { rentals: 48, enforcementAutostarts: 40 },
    { rentals: 50, enforcementAutostarts: 41 },
    { rentals: 50, enforcementAutostarts: 42 },
    { rentals: 51, enforcementAutostarts: 42 },
    { rentals: 52, enforcementAutostarts: 43 },
    { rentals: 52, enforcementAutostarts: 43 },
    { rentals: 53, enforcementAutostarts: 44 },
    { rentals: 54, enforcementAutostarts: 44 },
    { rentals: 55, enforcementAutostarts: 45 },
    { rentals: 55, enforcementAutostarts: 45 },
    { rentals: 56, enforcementAutostarts: 45 },
    { rentals: 56, enforcementAutostarts: 46 },
    { rentals: 56, enforcementAutostarts: 46 },
    { rentals: 57, enforcementAutostarts: 45 },
    { rentals: 57, enforcementAutostarts: 45 },
];

const fullSeries: DailyScansSeries = buildRelativeSeries(fullSeriesValues);

const sparseSeriesValues = [
    { rentals: 40, enforcementAutostarts: 28 },
    { rentals: 42, enforcementAutostarts: 31 },
    { rentals: 44, enforcementAutostarts: 32 },
    { rentals: 46, enforcementAutostarts: 34 },
    { rentals: 45, enforcementAutostarts: 33 },
];

// Sparse data with gaps: at offsets -13, -11, -8, -4, 0 days from today
const sparseSeries: DailyScansSeries = (() => {
    const endDate = new Date();
    endDate.setUTCHours(0, 0, 0, 0);
    const offsets = [13, 11, 8, 4, 0];
    return sparseSeriesValues.map((v, index) => {
        const date = new Date(endDate);
        date.setUTCDate(endDate.getUTCDate() - offsets[index]);
        return { date: date.toISOString().split('T')[0], ...v };
    });
})();

const SALES_CALL_TODAY_SCANNED_PLATES = 60;
const SALES_CALL_TODAY_AUTOSTARTS = 25;
const SALES_CALL_TODAY_AUTOSTART_REVENUE_CENTS = 49800;

function getSalesCallEndDate(currentDate = new Date()) {
    const endDate = new Date(currentDate);
    endDate.setUTCHours(0, 0, 0, 0);
    return endDate;
}

function buildGrowthSeries({
    days,
    rentalsStart,
    rentalsEnd,
    autostartsStart,
    autostartsEnd,
}: {
    days: number;
    rentalsStart: number;
    rentalsEnd: number;
    autostartsStart: number;
    autostartsEnd: number;
}): DailyScansSeries {
    const series: DailyScansSeries = [];
    const denominator = Math.max(1, days - 1);
    const salesCallEndDate = getSalesCallEndDate();

    for (let index = 0; index < days; index += 1) {
        const date = new Date(salesCallEndDate);
        date.setUTCDate(salesCallEndDate.getUTCDate() - (days - 1 - index));
        const progress = index / denominator;
        const rentals = Math.round(rentalsStart + (rentalsEnd - rentalsStart) * progress + (index % 9 === 0 ? 1 : 0));
        const enforcementAutostarts = Math.round(
            autostartsStart + (autostartsEnd - autostartsStart) * progress + (index % 11 === 0 ? 1 : 0)
        );

        series.push({
            date: date.toISOString().split('T')[0],
            rentals,
            enforcementAutostarts,
        });
    }

    return series;
}

function buildCycleSeries({
    days,
    rentalsStart,
    rentalsDip,
    rentalsEnd,
    autostartsStart,
    autostartsDip,
    autostartsEnd,
}: {
    days: number;
    rentalsStart: number;
    rentalsDip: number;
    rentalsEnd: number;
    autostartsStart: number;
    autostartsDip: number;
    autostartsEnd: number;
}): DailyScansSeries {
    const series: DailyScansSeries = [];
    const denominator = Math.max(1, days - 1);
    const dipAtProgress = 0.28;
    const salesCallEndDate = getSalesCallEndDate();

    for (let index = 0; index < days; index += 1) {
        const date = new Date(salesCallEndDate);
        date.setUTCDate(salesCallEndDate.getUTCDate() - (days - 1 - index));
        const progress = index / denominator;
        const wave = Math.sin((index / 11) * Math.PI) * 0.7;

        const interpolation = (startValue: number, dipValue: number, endValue: number) => {
            if (progress <= dipAtProgress) {
                const phase = progress / dipAtProgress;
                return startValue + (dipValue - startValue) * phase;
            }

            const phase = (progress - dipAtProgress) / (1 - dipAtProgress);
            return dipValue + (endValue - dipValue) * phase;
        };

        const rentals = Math.round(interpolation(rentalsStart, rentalsDip, rentalsEnd) + wave);
        const enforcementAutostarts = Math.round(
            interpolation(autostartsStart, autostartsDip, autostartsEnd) + wave * 0.8
        );

        series.push({
            date: date.toISOString().split('T')[0],
            rentals,
            enforcementAutostarts,
        });
    }

    return series;
}

function buildSalesCallStats(series: DailyScansSeries, seriesGranularity: 'day' | 'week'): EnforcementStats {
    return {
        total_scanned_plates_today: SALES_CALL_TODAY_SCANNED_PLATES,
        total_enforcement_autostart_today: SALES_CALL_TODAY_AUTOSTARTS,
        enforcement_autostart_revenue_today: SALES_CALL_TODAY_AUTOSTART_REVENUE_CENTS,
        series_granularity: seriesGranularity,
        daily_scans: buildDailyScans(series),
    };
}

const mockEnforcementStatsByLookback: Record<MockScansLookbackPeriod, EnforcementStats> = {
    '4w': buildSalesCallStats(
        buildGrowthSeries({
            days: 28,
            rentalsStart: 45,
            rentalsEnd: 57,
            autostartsStart: 38,
            autostartsEnd: 45,
        }),
        'day'
    ),
    '3m': buildSalesCallStats(
        buildGrowthSeries({
            days: 90,
            rentalsStart: 38,
            rentalsEnd: 57,
            autostartsStart: 31,
            autostartsEnd: 45,
        }),
        'week'
    ),
    '6m': buildSalesCallStats(
        buildGrowthSeries({
            days: 180,
            rentalsStart: 30,
            rentalsEnd: 57,
            autostartsStart: 23,
            autostartsEnd: 45,
        }),
        'week'
    ),
    '12m': buildSalesCallStats(
        buildCycleSeries({
            days: 365,
            rentalsStart: 40,
            rentalsDip: 30,
            rentalsEnd: 58,
            autostartsStart: 32,
            autostartsDip: 24,
            autostartsEnd: 45,
        }),
        'week'
    ),
};

export function getMockEnforcementStats(lookback: MockScansLookbackPeriod): EnforcementStats {
    const stats = mockEnforcementStatsByLookback[lookback] ?? mockEnforcementStatsByLookback['12m'];
    return stats.series_granularity === 'week' ? buildWeeklyStats(stats) : stats;
}

export const mockEnforcementStatsFull: EnforcementStats = {
    total_scanned_plates_today: SALES_CALL_TODAY_SCANNED_PLATES,
    total_enforcement_autostart_today: SALES_CALL_TODAY_AUTOSTARTS,
    enforcement_autostart_revenue_today: SALES_CALL_TODAY_AUTOSTART_REVENUE_CENTS,
    series_granularity: 'day',
    daily_scans: buildDailyScans(fullSeries),
};

export const mockEnforcementStatsSparse: EnforcementStats = {
    total_scanned_plates_today: 19,
    total_enforcement_autostart_today: 3,
    enforcement_autostart_revenue_today: 13200,
    series_granularity: 'day',
    daily_scans: buildDailyScans(sparseSeries),
};

export const mockEnforcementStatsEmpty: EnforcementStats = {
    total_scanned_plates_today: 0,
    total_enforcement_autostart_today: 0,
    enforcement_autostart_revenue_today: 0,
    series_granularity: 'day',
    daily_scans: {},
};
